import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FaEye, FaEyeSlash, FaBuilding } from 'react-icons/fa';
import BrandLogo from '../../components/common/BrandLogo';
import { MdError } from 'react-icons/md';
import useAuth from '../../hooks/useAuth';
import { useTenant } from '../../context/TenantContext';
import * as authService from '../../services/authService';
import api from '../../services/api';
import useForceDarkTheme from '../../hooks/useForceDarkTheme';

// ── Multi-club picker ────────────────────────────────────────────────────────
const ROLE_STYLE = {
  admin:   { bg: '#fef2f2', color: '#dc2626', label: 'Admin'   },
  trainer: { bg: '#eff6ff', color: '#2563eb', label: 'Trainer' },
  student: { bg: '#f0fdf4', color: '#16a34a', label: 'Student' },
};

function ClubPicker({ clubs, onSelect, onBack, loading, error }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-[#141312]">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <BrandLogo size={48} className="mb-3" />
          <span className="text-sm font-semibold text-[#8a847c]">TrainAxis</span>
        </div>

        <h1 className="text-xl font-bold text-[#ede8e0] text-center mb-1">Choose your club</h1>
        <p className="text-sm text-[#8a847c] text-center mb-6">
          You have access to multiple clubs. Which one would you like to open?
        </p>

        <div className="space-y-3">
          {clubs.map(c => {
            const style = ROLE_STYLE[c.role] || ROLE_STYLE.student;
            return (
              <button
                key={c.clubId}
                onClick={() => onSelect(c)}
                disabled={loading}
                className="w-full text-left p-4 bg-[#1e1d1b] border border-[rgba(237,232,224,0.08)] rounded-2xl hover:border-[rgba(237,232,224,0.15)] transition-all disabled:opacity-60 group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: '#fef2f2' }}>
                    <FaBuilding className="text-sm" style={{ color: '#dc2626' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#ede8e0] text-sm truncate">{c.clubName}</div>
                    <div className="text-xs text-[#8a847c] mt-0.5">{c.clubSlug}</div>
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ background: style.bg, color: style.color }}>
                    {style.label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {error && (
          <div className="mt-4 flex items-start gap-2 bg-red-400/10 text-red-400 rounded-xl p-3 text-sm">
            <MdError className="text-base flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <button
          onClick={onBack}
          disabled={loading}
          className="mt-6 w-full py-2.5 text-sm bg-transparent text-[#c5bfb5] border border-[rgba(237,232,224,0.10)] rounded-full hover:bg-[#28261f] transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
        >
          ← Use a different account
        </button>
      </div>
    </div>
  );
}

// ── OTP (2FA) step ───────────────────────────────────────────────────────────
// Reached from EITHER the password form (single-club admin) or the club picker
// (multi-club admin entering a 2FA club). Verifying completes login for that club.
function OtpStep({ context, code, onCodeChange, onVerify, onResend, onBack, verifying, resending, resendSeconds, error }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-[#141312]">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <BrandLogo size={48} className="mb-3" />
          <span className="text-sm font-semibold text-[#8a847c]">TrainAxis</span>
        </div>

        <h1 className="text-xl font-bold text-[#ede8e0] text-center mb-1">Enter your sign-in code</h1>
        <p className="text-sm text-[#8a847c] text-center mb-6">
          We emailed a 6-digit code to finish signing in
          {context ? <> to <span className="font-medium text-[#8a847c]">{context}</span></> : ''}.
        </p>

        <form onSubmit={onVerify} className="space-y-4">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            value={code}
            onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="w-full px-3.5 py-3 text-center text-2xl tracking-[0.4em] font-semibold bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl text-[#ede8e0] placeholder-[#6b6560] outline-none focus:border-[rgba(237,232,224,0.15)] focus:outline-none"
          />

          {error && (
            <div className="flex items-start gap-2 bg-red-400/10 text-red-400 rounded-xl p-3 text-sm">
              <MdError className="text-base flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={code.length < 6 || verifying}
            className="w-full py-2.5 rounded-full font-medium text-white text-sm transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ background: '#E24B4A' }}
          >
            {verifying && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin flex-shrink-0" />}
            {verifying ? 'Verifying...' : 'Verify & sign in'}
          </button>
        </form>

        <div className="mt-5 flex items-center justify-center gap-1 text-sm">
          <span className="text-[#8a847c]">Didn&apos;t get it?</span>
          <button
            onClick={onResend}
            disabled={resendSeconds > 0 || resending}
            className="font-semibold text-primary hover:text-primary-dark disabled:no-underline disabled:text-[#6b6560] disabled:cursor-not-allowed"
          >
            {resendSeconds > 0 ? `Resend in ${resendSeconds}s` : (resending ? 'Sending...' : 'Resend code')}
          </button>
        </div>

        <button
          onClick={onBack}
          disabled={verifying}
          className="mt-6 w-full py-2.5 text-sm bg-transparent text-[#c5bfb5] border border-[rgba(237,232,224,0.10)] rounded-full hover:bg-[#28261f] transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
        >
          ← Back
        </button>
      </div>
    </div>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Main Login ───────────────────────────────────────────────────────────────
export default function Login() {
  useForceDarkTheme();
  document.title = 'Sign In — TrainAxis';
  const [email,         setEmail]         = useState('');
  const [password,      setPassword]      = useState('');
  const [showPwd,       setShowPwd]       = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [multiClubs,    setMultiClubs]    = useState(null);
  const [emailTouched,  setEmailTouched]  = useState(false);
  const [pwTouched,     setPwTouched]     = useState(false);
  // 2FA (email-OTP) step — reachable from the password form OR the club picker.
  const [otpToken,      setOtpToken]      = useState(null);
  const [otpCode,       setOtpCode]       = useState('');
  const [otpContext,    setOtpContext]    = useState('');
  const [otpError,      setOtpError]      = useState('');
  const [verifying,     setVerifying]     = useState(false);
  const [resending,     setResending]     = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  const { login }                       = useAuth();
  const { club, setClubDirectly }       = useTenant();
  const navigate                        = useNavigate();

  const accent = club?.primaryColor || '#E24B4A';

  // Tick down the resend cooldown while the OTP step is showing.
  useEffect(() => {
    if (!otpToken || resendSeconds <= 0) return;
    const t = setTimeout(() => setResendSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpToken, resendSeconds]);

  // ── Field-level validation ─────────────────────────────────────────────────
  const emailTrimmed = email.trim();
  const emailFormatOk = EMAIL_RE.test(emailTrimmed);
  const emailErr = emailTouched
    ? !emailTrimmed          ? 'Email is required.'
    : !emailFormatOk         ? 'Enter a valid email address.'
    : ''
    : '';
  const pwErr = pwTouched && !password ? 'Password is required.' : '';
  const formValid = !!emailTrimmed && emailFormatOk && !!password;

  const finishLogin = (data) => {
    setClubDirectly(data.club, data.club.slug);
    login(data.user, data.token);
    if (data.mustChangePassword) {
      navigate('/change-password', { replace: true });
    } else {
      toast.success(`Welcome back, ${data.user.name}!`);
      navigate(data.redirectUrl, { replace: true });
    }
  };

  // Switch either entry point (password form / club picker) into the OTP step.
  const enterOtp = (token, context) => {
    setOtpToken(token);
    setOtpCode('');
    setOtpError('');
    setError('');
    setOtpContext(context || '');
    setResendSeconds(60);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otpCode.length < 6 || verifying) return;
    setVerifying(true);
    setOtpError('');
    try {
      // Returns the normal login payload for THAT club → same finishLogin as every other path.
      const { data } = await api.post('/api/auth/verify-login-otp', { otpToken, code: otpCode });
      finishLogin(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401)      setOtpError('Your sign-in session expired. Go back and sign in again.');
      else if (status === 400) setOtpError(err.response?.data?.message || 'Invalid or expired code.');
      else if (status === 429) setOtpError('Too many attempts. Please wait and try again.');
      else if (!err.response)  setOtpError('Cannot reach server. Check your connection.');
      else                     setOtpError('Something went wrong. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendSeconds > 0 || resending) return;
    setResending(true);
    setOtpError('');
    try {
      await api.post('/api/auth/resend-login-otp', { otpToken });
      setResendSeconds(60);
      toast.success('A new code is on its way.');
    } catch (err) {
      const secs = err.response?.data?.secondsLeft;
      if (secs) setResendSeconds(secs);       // sync to the server's remaining cooldown
      else setOtpError(err.response?.data?.message || 'Could not resend the code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // Back from OTP: clearing otpToken returns to whichever screen we came from —
  // the club picker (if multiClubs is still set) or the password form (if not).
  const handleOtpBack = () => {
    setOtpToken(null);
    setOtpCode('');
    setOtpError('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Mark all fields as touched to surface any hidden errors
    setEmailTouched(true);
    setPwTouched(true);
    // Block API call if form is invalid or already in flight
    if (!formValid || loading) return;
    setError('');
    setLoading(true);
    try {
      const { data } = await authService.smartLogin(emailTrimmed.toLowerCase(), password);
      if (data.multipleClubs) {
        setMultiClubs(data.clubs);
        return;
      }
      if (data.twoFactorRequired) {
        enterOtp(data.otpToken, club?.name || 'your TrainAxis account');
        return;
      }
      finishLogin(data);
    } catch (err) {
      const status = err.response?.status;
      const code   = err.response?.data?.code;
      if (code === 'APPROVAL_PENDING')    navigate('/pending-approval');
      else if (status === 401)            setError('Invalid email or password');
      else if (status === 403 && code === 'EMAIL_NOT_VERIFIED') setError('Please verify your email before logging in.');
      else if (status === 403 && code === 'APPROVAL_REJECTED')  setError(err.response.data.message || 'Your account application was rejected.');
      else if (status === 403)            setError('Your account has been suspended. Contact your administrator.');
      else if (status === 429)            setError('Too many attempts. Please wait 15 minutes before trying again.');
      else if (status >= 500)             setError('Something went wrong. Please try again.');
      else if (!err.response)             setError('Cannot reach server. Check your connection.');
      else setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClubSelect = async (selectedClub) => {
    setError('');
    setLoading(true);
    try {
      const { data } = await authService.loginToClub(email, password, selectedClub.clubId);
      if (data.twoFactorRequired) {
        enterOtp(data.otpToken, selectedClub.clubName);
        return;
      }
      finishLogin(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) setError('Session expired — please go back and sign in again.');
      else setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (otpToken) {
    return (
      <OtpStep
        context={otpContext}
        code={otpCode}
        onCodeChange={setOtpCode}
        onVerify={handleVerifyOtp}
        onResend={handleResendOtp}
        onBack={handleOtpBack}
        verifying={verifying}
        resending={resending}
        resendSeconds={resendSeconds}
        error={otpError}
      />
    );
  }

  if (multiClubs) {
    return (
      <ClubPicker
        clubs={multiClubs}
        onSelect={handleClubSelect}
        onBack={() => { setMultiClubs(null); setError(''); }}
        loading={loading}
        error={error}
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-[#141312]">
      <div className="w-full max-w-sm">

        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <BrandLogo size={48} className="mb-3" />
          <span className="text-sm font-semibold text-[#8a847c]">TrainAxis</span>
        </div>

        <h1 className="text-2xl font-bold text-[#ede8e0] text-center mb-1">Sign in</h1>
        <p className="text-sm text-[#8a847c] text-center mb-8">
          {club ? `to ${club.name}` : 'to continue to TrainAxis'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Email */}
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-[#ede8e0] mb-1.5">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              onBlur={() => setEmailTouched(true)}
              placeholder="you@example.com"
              autoFocus
              autoComplete="email"
              aria-invalid={!!emailErr}
              aria-describedby={emailErr ? 'email-error' : undefined}
              className="w-full px-3.5 py-2.5 text-sm bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl text-[#ede8e0] placeholder-[#6b6560] outline-none transition-all focus:border-[rgba(237,232,224,0.15)] focus:outline-none"
              style={{ borderColor: emailErr ? '#ef4444' : '#e5e7eb' }}
              onFocus={e  => (e.target.style.borderColor = emailErr ? '#ef4444' : accent)}
            />
            {emailErr && (
              <p id="email-error" className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <MdError className="text-sm flex-shrink-0" /> {emailErr}
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="login-password" className="text-sm font-medium text-[#ede8e0]">Password</label>
              <Link
                to="/forgot-password"
                className="text-xs font-medium hover:underline"
                style={{ color: accent }}
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="login-password"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                onBlur={() => setPwTouched(true)}
                placeholder="••••••••"
                autoComplete="current-password"
                aria-invalid={!!pwErr}
                aria-describedby={pwErr ? 'password-error' : undefined}
                className="w-full px-3.5 py-2.5 pr-11 text-sm bg-[#1a1918] border border-[rgba(237,232,224,0.08)] rounded-xl text-[#ede8e0] placeholder-[#6b6560] outline-none transition-all focus:border-[rgba(237,232,224,0.15)] focus:outline-none"
                style={{ borderColor: pwErr ? '#ef4444' : '#e5e7eb' }}
                onFocus={e => (e.target.style.borderColor = pwErr ? '#ef4444' : accent)}
              />
              <button
                type="button"
                onClick={() => setShowPwd(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: '#9ca3af' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#6b7280')}
                onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
              >
                {showPwd ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
              </button>
            </div>
            {pwErr && (
              <p id="password-error" className="mt-1 text-xs text-red-500 flex items-center gap-1">
                <MdError className="text-sm flex-shrink-0" /> {pwErr}
              </p>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 bg-red-400/10 text-red-400 rounded-xl p-3 text-sm">
              <MdError className="text-base flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!formValid || loading}
            className="w-full py-2.5 rounded-full font-medium text-white text-sm transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ background: accent }}
          >
            {loading && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin flex-shrink-0" />}
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <div className="h-px bg-[rgba(237,232,224,0.06)] my-6" />

        <p className="text-center text-sm text-[#8a847c]">
          Don&apos;t have an account?{' '}
          <Link to="/signup" className="font-semibold hover:underline" style={{ color: accent }}>
            Start free trial
          </Link>
        </p>
      </div>

      <p className="mt-10 text-xs text-[#6b6560]">© {new Date().getFullYear()} TrainAxis</p>
    </div>
  );
}
